// Follow-ups — #331 stage 2.
//
// WHAT THIS IS NOT
// ================
// It is not an unread count. `services/ea/email_followup.py` opens with the
// reason: "most unread mail is not work, and a brief that says 'you have 41
// unread' has told its owner nothing they did not already dread." The backend
// flags a message only when it scores on one of seven named signals — a direct
// question, a requested update, a promised response, a deadline, an unresolved
// commitment, sender importance, elapsed time — and attaches the signals it
// hit as `why`. That sentence is rendered on every row for the same reason the
// priority reason is: without it this is just another inbox.
//
// THE THREE OUTCOMES ARE DIFFERENT AND MUST LOOK DIFFERENT
// ========================================================
// `scan()` returns `available: false` with a reason when it cannot read mail at
// all, and `available: true` with an empty list when it read the inbox and
// found nothing that needs the owner. Those mean opposite things. Collapsing
// them into one empty state would tell the owner "nothing needs you" when the
// truth is "nobody looked" — CLAUDE.md Part 2 §17.6. They are rendered as two
// distinct states here, and a failed request is a third.
//
// WHY THE UNAVAILABLE STATE DOES NOT NAME A CAUSE
// ===============================================
// `scan()` returns `available: false` for any failure of the Google read — not
// connected, token expired, or the API itself failing. Asserting "your Google
// account is not connected" would be an inference presented as a diagnosis. The
// surface says what is true (the mail could not be read), shows the reason the
// server gave, and leaves the conclusion to the owner.
import React from "react";

import EmptyState from "../../components/EmptyState";
import PanelError from "./PanelError";
import { ENDPOINTS } from "./api";
import usePanelData from "./usePanelData";

/** How long it has been waiting, as the backend measured it. */
export function waitingLabel(ageDays) {
  if (ageDays === null || ageDays === undefined) return null;
  const n = Number(ageDays);
  if (Number.isNaN(n)) return null;
  const whole = Math.floor(n);
  if (whole < 1) return "Arrived today";
  return `Waiting ${whole} day${whole === 1 ? "" : "s"}`;
}

export default function FollowUps() {
  const { status, data, error } = usePanelData(ENDPOINTS.followups);

  if (status === "loading") {
    return <div className="ea-panel-loading dim" role="status">Reading your mail…</div>;
  }
  if (status === "disabled") {
    return (
      <EmptyState
        title="Not enabled"
        detail="Your assistant is not switched on for this account."
      />
    );
  }
  if (status === "denied") {
    return (
      <EmptyState
        title="Not yours to see"
        detail="Mail belongs to the account that owns the assistant."
      />
    );
  }
  if (status === "error") {
    return <PanelError error={error} />;
  }

  // The read succeeded, but the mailbox could not be opened. This is NOT an
  // empty inbox and must not be dressed as one.
  if (data && data.available === false) {
    return (
      <div className="ea-panel-unavailable" role="status">
        <div className="ea-panel-unavailable-title">Your mail could not be read</div>
        <div className="ea-panel-unavailable-detail dim">
          Nothing was scanned, so this is not an empty list — it is an unknown one.
          If your Google account is not connected yet, connecting it is what fills
          this screen.
        </div>
        {data.reason ? (
          <div className="ea-panel-unavailable-reason dim">
            What the server reported: {data.reason}
          </div>
        ) : null}
      </div>
    );
  }

  const items = (data && data.followups) || [];
  const scanned = (data && data.scanned) || 0;

  if (!items.length) {
    return (
      <EmptyState
        title="Nothing is waiting on you"
        detail={
          `${scanned} recent message${scanned === 1 ? "" : "s"} scanned. None of them ` +
          "asks anything of you — no question, deadline, promise or open commitment."
        }
      />
    );
  }

  return (
    <div className="ea-panel">
      <p className="ea-panel-basis dim">
        {items.length} of {scanned} recent message{scanned === 1 ? "" : "s"} need you.
      </p>

      <ul className="ea-follow-list">
        {items.map((item, i) => {
          const waiting = waitingLabel(item.age_days);
          return (
            <li className="ea-follow" key={item.message_id || `${item.subject}-${i}`}>
              <div className="ea-follow-subject">{item.subject || "(no subject)"}</div>
              <div className="ea-follow-from dim">{item.from}</div>
              {/* Why this one, and not the other forty. */}
              <div className="ea-follow-why">{item.why}</div>
              {waiting ? <div className="ea-follow-age dim">{waiting}</div> : null}
            </li>
          );
        })}
      </ul>

      <p className="ea-panel-foot dim">
        Flagged from the subject and preview of each message. Your assistant never
        sends, replies, archives or marks anything read.
      </p>
    </div>
  );
}
